/**
 * The one call a pickup cart makes. Everything before it lives in the browser,
 * because an order that exists is an order the kitchen cooks.
 *
 * The refusal shape matters as much as the success one: the server returns the
 * cart it corrected, and the client replaces localStorage with it. A refusal
 * that only said "no" would leave the diner tapping pay on the same cart the
 * server just rejected.
 */

export type PickupCheckoutChange = {
  kind: string
  item_id: number | null
  name: string | null
  price: string | null
}

export type PickupCheckoutServerLine = {
  item_id: number
  name: string
  quantity: number
  price: string
  base_price: string
  modifiers_price: string
  modifiers: { id: number; name: string; price: string }[]
  currency: string
  station: string
  notes: string | null
}

export type PickupCheckoutResult =
  | { ok: true; checkoutUrl: string }
  | { ok: false; reason: string; changes: PickupCheckoutChange[]; cart: PickupCheckoutServerLine[] }

export async function createPickupCheckout({
  slug,
  cart,
  idempotencyKey,
  couponCode,
}: {
  slug: string
  cart: unknown[]
  idempotencyKey: string
  /** The code the diner typed, already normalised. Omitted when there is none. */
  couponCode?: string | null
}): Promise<PickupCheckoutResult> {
  const response = await fetch(`/r/${encodeURIComponent(slug)}/checkout`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    // A sibling of `cart`, never a member of it. `cart_params` re-reads every
    // permitted value off each line, and a code the server resolves itself is
    // exactly what must not travel through that path.
    body: JSON.stringify({
      cart,
      idempotency_key: idempotencyKey,
      ...(couponCode ? { coupon_code: couponCode } : {}),
    }),
  })

  const payload = await parseJson(response)

  if (response.ok && typeof payload.checkoutUrl === "string") {
    return { ok: true, checkoutUrl: payload.checkoutUrl }
  }

  return {
    ok: false,
    // `network_error` covers a response that carried no reason at all — a proxy
    // page, a 500, a dropped connection. The screen still has something to say.
    reason: typeof payload.reason === "string" ? payload.reason : "network_error",
    changes: Array.isArray(payload.changes) ? (payload.changes as PickupCheckoutChange[]) : [],
    cart: Array.isArray(payload.cart) ? (payload.cart as PickupCheckoutServerLine[]) : [],
  }
}

/**
 * What a code is worth against this cart, before the diner commits to paying.
 *
 * A quote, not a redemption: the endpoint spends no use and writes no
 * redemption. The checkout POST re-asks all of it under the coupon's row lock,
 * which is why the amount here is for reading and never for charging.
 *
 * It exists because "ya venció" and "ya se usó" are different sentences, and a
 * code that can only be tested by attempting a payment is a code nobody tests.
 */
export type PickupCouponResult =
  | { ok: true; code: string; promotionName: string; discountAmount: string }
  | { ok: false; reason: string; message: string | null }

export async function quotePickupCoupon({
  slug,
  cart,
  code,
}: {
  slug: string
  cart: unknown[]
  code: string
}): Promise<PickupCouponResult> {
  const response = await fetch(`/r/${encodeURIComponent(slug)}/coupon`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ cart, code }),
  })

  const payload = await parseJson(response)

  if (
    response.ok &&
    typeof payload.code === "string" &&
    typeof payload.discountAmount === "string"
  ) {
    return {
      ok: true,
      code: payload.code,
      promotionName: typeof payload.promotionName === "string" ? payload.promotionName : "",
      discountAmount: payload.discountAmount,
    }
  }

  return {
    ok: false,
    reason: typeof payload.reason === "string" ? payload.reason : "network_error",
    // The server writes the sentence, because the server is the only thing that
    // knows whether this code expired or was already spent — and telling the two
    // apart is the whole acceptance criterion. The client keeps a fallback only
    // for a response that carried no message at all.
    message: typeof payload.message === "string" ? payload.message : null,
  }
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

async function parseJson(response: Response) {
  try {
    return (await response.json()) as Record<string, unknown>
  } catch {
    return {}
  }
}
